import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Avatar,
	BeakerOutline,
	BookmarkOutline,
	Box,
	ChevronRightOutline,
	clearThemeStorage,
	CogOutline,
	DotsHorizontalOutline,
	DownloadOutline,
	HStack,
	LifeRaftOutline,
	LogoutOutline,
	MailOutline,
	QuestionMarkCircleAltOutline,
	rem,
	Text,
} from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import { useSeasonalTheme } from '../../../components/seasonal/seasonal-theme-context';
import { resetGlobalSyncState } from '../../../hooks/use-user-color-mode-with-sync';
import { AnalyticsEvent } from '../../../lib/analytics/events';
import { showSpace } from '../../../lib/analytics/intercom';
import { useAnalytics } from '../../../lib/analytics/use-analytics';
// eslint-disable-next-line local-rules/no-cross-module-imports
import { useProfilePath } from '../../profile/hooks/use-profile-path';
import { CustomThemeMenuItem } from './custom-theme-menu-item';
import { IconAndTextContent } from './icon-and-text-content';
import { NotificationMenuItem } from './notification';
import { ThemeMenuItem } from './theme-menu-item';
import { UserNavDivider } from './user-nav-divider';
import { UserNavFooterLink } from './user-nav-footer-link';
import { UserNavLink } from './user-nav-link';
import { UserNavLinkExternal } from './user-nav-link-external';
import { UserNavMenu } from './user-nav-menu';

type UserMenuProps = {
	isExpanded: boolean;
	onOpenNotifications: () => void;
	onMenuOpen?: () => void;
	unreadCount: number;
};

export const UserMenu: FC<UserMenuProps> = ({ isExpanded, onOpenNotifications, onMenuOpen, unreadCount }) => {
	const { t } = useTranslation('navigation');
	const { track } = useAnalytics();
	const user = useCurrentUser();
	const { getProfilePath } = useProfilePath();
	const { isSeasonalThemeEnabled } = useSeasonalTheme();
	const handleLogoutClick = useCallback(() => {
		// Clear theme localStorage to prevent theme settings from persisting
		// across different user accounts on the same browser
		clearThemeStorage();
		// Reset the global sync state so the next user's preferences will be synced
		resetGlobalSyncState();
	}, []);

	return (
		<UserNavMenu
			buttonIcon={
				<Box position="relative" display="flex" alignItems="center">
					<Avatar name={user.display_name} src={user.profile_pic_url} h={rem(24)} w={rem(24)} fontSize={1} />
					{unreadCount > 0 && <Box position="absolute" top={rem(-4)} right={rem(-4)} h={rem(10)} w={rem(10)} bg="error.inputBorder" borderRadius="full" />}
				</Box>
			}
			buttonContents={
				<HStack justifyContent="space-between" alignItems="flex-start" width="100%" data-testid="user-profile-button">
					<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
						{user.display_name}
					</Text>
					<DotsHorizontalOutline size="md" />
				</HStack>
			}
			isExpanded={isExpanded}
			placement="right-start"
			onOpen={() => {
				track(AnalyticsEvent.UserNavbar.UserNavigation);
				onMenuOpen?.();
			}}
		>
			{/* VIEW PROFILE */}
			<UserNavLink to={getProfilePath(user.id)} onClick={() => track(AnalyticsEvent.UserNavbar.ViewProfileNavigation)} data-testid="nav-link-view-profile">
				<IconAndTextContent
					icon={<Avatar name={user.display_name} src={user.profile_pic_url} h={rem(24)} w={rem(24)} fontSize={1} />}
					label={t('user_menu.view_profile')}
					isExpanded
				/>
			</UserNavLink>

			{/* PROFILE SETTINGS */}
			<UserNavLink to="/user/settings" onClick={() => track(AnalyticsEvent.UserNavbar.ProfileSettingsNavigation)} data-testid="nav-link-profile-settings">
				<IconAndTextContent icon={<CogOutline size="md" />} label={t('user_menu.profile_settings')} isExpanded />
			</UserNavLink>

			{/* BOOKMARKS */}
			<UserNavLink to="/bookmark" onClick={() => track(AnalyticsEvent.UserNavbar.BookmarksNavigation)} data-testid="nav-link-bookmarks">
				<IconAndTextContent icon={<BookmarkOutline size="md" />} label={t('user_menu.bookmarks')} isExpanded />
			</UserNavLink>

			{/* NOTIFICATIONS */}
			<NotificationMenuItem
				isExpanded
				onClick={() => {
					track(AnalyticsEvent.UserNavbar.NotificationsNavigation);
					onOpenNotifications();
				}}
			/>

			<UserNavDivider />

			{/* THEME - hidden when seasonal theme overrides color mode */}
			{!isSeasonalThemeEnabled && (
				<>
					<ThemeMenuItem isExpanded />
					<CustomThemeMenuItem isExpanded />
					<UserNavDivider />
				</>
			)}

			{/* HELP SUBMENU */}
			<UserNavMenu
				buttonIcon={<QuestionMarkCircleAltOutline size="md" />}
				buttonContents={
					<HStack justifyContent="space-between" alignItems="center" width="100%">
						<Text fontSize={100} fontWeight="medium" whiteSpace="nowrap">
							{t('user_menu.help')}
						</Text>
						<ChevronRightOutline size="sm" />
					</HStack>
				}
				isExpanded
				isSubmenu
				placement="right-start"
				offset={[0, 0]}
			>
				{/* CONTACT US */}
				<UserNavLinkExternal href="https://bonusly.com/company/contact" data-testid="nav-link-contact-us">
					<IconAndTextContent icon={<MailOutline size="md" />} label={t('user_menu.contact')} isExpanded />
				</UserNavLinkExternal>

				{/* HELP CENTER */}
				<UserNavLinkExternal href="https://help.bonus.ly/en/" data-testid="nav-link-help-center">
					<IconAndTextContent icon={<LifeRaftOutline size="md" />} label={t('user_menu.helpCenter')} isExpanded />
				</UserNavLinkExternal>

				{/* WHAT'S NEW */}
				<UserNavLinkExternal onClick={() => showSpace('news')} data-testid="nav-link-whats-new">
					<IconAndTextContent icon={<BeakerOutline size="md" />} label={t('user_menu.whatsNew')} isExpanded />
				</UserNavLinkExternal>

				<UserNavDivider />

				{/* ANDROID APP */}
				<UserNavLinkExternal href="https://play.google.com/store/apps/details?id=ly.bonus.android" data-testid="nav-link-android-app">
					<IconAndTextContent icon={<DownloadOutline size="md" />} label={t('user_menu.androidApp')} isExpanded />
				</UserNavLinkExternal>

				{/* iOS APP */}
				<UserNavLinkExternal href="https://apps.apple.com/us/app/bonusly/id995249036" data-testid="nav-link-ios-app">
					<IconAndTextContent icon={<DownloadOutline size="md" />} label={t('user_menu.iosApp')} isExpanded />
				</UserNavLinkExternal>

				<UserNavDivider />

				{/* STATUS PAGE */}
				<UserNavFooterLink href="https://status.bonus.ly/" mt={rem(8)} data-testid="nav-link-status-page">
					{t('user_menu.statusPage')}
				</UserNavFooterLink>

				{/* TERMS OF SERVICE */}
				<UserNavFooterLink href="https://bonusly.com/terms" data-testid="nav-link-terms-of-service">
					{t('user_menu.termsOfService')}
				</UserNavFooterLink>

				{/* PRIVACY POLICY */}
				<UserNavFooterLink href="https://bonus.ly/privacy_policy" mb={rem(8)} data-testid="nav-link-privacy-policy">
					{t('user_menu.privacyPolicy')}
				</UserNavFooterLink>
			</UserNavMenu>

			{/* LOG OUT */}
			<UserNavLink to="/users/sign_out" isRecognizeRoute={true} onClick={handleLogoutClick} data-testid="nav-link-logout">
				<IconAndTextContent icon={<LogoutOutline size="md" />} label={t('user_menu.log_out')} isExpanded />
			</UserNavLink>
		</UserNavMenu>
	);
};
